// Blog-Automatik tab: approval queue for channel='blog' items + motor trigger.
import type { MarketingDeps } from "./marketing.js";

export interface ContentItem {
  id: number; profile: string; channel: string; type: string; status: string;
  title: string | null; body_text: string | null; target_url: string | null;
  approved_at?: string | null; published_at?: string | null; created_at: string;
  external_ref?: string | null;
  /** Anatomie-Befund. null = nie geprueft (z. B. vor deren Einfuehrung). */
  pruefung?: { ok: number; gelaufen: number; probleme: string | null } | null;
  /** Themenbindung — eigene Frage, eigener Durchgang. */
  pruefung_thema?: { ok: number; gelaufen: number; probleme: string | null } | null;
  /** Presenter-Videos: fester Hintergrund statt Modellurteil, Name in `probleme`. */
  pruefung_hintergrund?: { ok: number; gelaufen: number; probleme: string | null } | null;
}

// Publish-Automatik: Cron läuft am 1. + 15. um 06:30 (Serverzeit), veröffentlicht
// pro Lauf GENAU 1 freigegebenen Artikel (FIFO nach id), sofern das Portal in den
// letzten 30 Tagen noch < 2 neue Artikel bekommen hat (Kadenz 2/Portal/30 T).
const STATUS_ORDER = ["review", "draft", "needs_fix", "rejected", "approved", "published"] as const;
const STATUS_LABEL: Record<string, string> = {
  review: "Wartet auf Freigabe", draft: "Entwürfe/Themen", approved: "Freigegeben",
  published: "Veröffentlicht", needs_fix: "Fehler beim Veröffentlichen", rejected: "Abgelehnt",
};

const PORTAL_RE = /\[(WIP|SWAV|WHI|FASZ)\]\s*$/i;
const portalOf = (title: string | null): string => {
  const m = title?.match(PORTAL_RE);
  return m && m[1] ? m[1].toUpperCase() : "?";
};

const fmtDate = (v: Date | string | null | undefined): string => {
  if (!v) return "–";
  const d = v instanceof Date ? v : new Date(v);
  if (isNaN(d.getTime())) return "–";
  return d.toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric" });
};

// Nächste Cron-Slots (1. + 15. jeden Monats, 06:30) ab `from`, chronologisch.
const nextSlots = (from: Date, n: number): Date[] => {
  const out: Date[] = [];
  let y = from.getFullYear(), mo = from.getMonth();
  while (out.length < n) {
    for (const day of [1, 15]) {
      const d = new Date(y, mo, day, 6, 30, 0, 0);
      if (d.getTime() > from.getTime()) out.push(d);
    }
    mo++; if (mo > 11) { mo = 0; y++; }
  }
  return out.slice(0, n);
};

// Prognose: modelliert das echte Cron-Verhalten — pro Slot (1. + 15.) max 1 Artikel
// PRO PORTAL (FIFO nach id), Kadenz-Obergrenze 2/Portal/30 Tage (rollierend).
const forecast = (approved: ContentItem[]): Map<number, Date> => {
  const result = new Map<number, Date>();
  const queue = [...approved].sort((a, b) => a.id - b.id);
  if (!queue.length) return result;
  const slots = nextSlots(new Date(), queue.length * 2 + 12);
  const WINDOW = 30 * 24 * 3600 * 1000;
  const hist: Record<string, number[]> = {};
  const remaining = new Set(queue.map((a) => a.id));
  for (const slot of slots) {
    if (!remaining.size) break;
    const portalDoneThisSlot = new Set<string>();
    for (const art of queue) {
      if (!remaining.has(art.id)) continue;
      const p = portalOf(art.title);
      if (portalDoneThisSlot.has(p)) continue;            // max 1/Portal/Slot
      const h = hist[p] ?? (hist[p] = []);
      const recent = h.filter((t) => slot.getTime() - t < WINDOW).length;
      if (recent >= 2) continue;                          // Kadenz 2/Portal/30 T
      result.set(art.id, slot);
      h.push(slot.getTime());
      portalDoneThisSlot.add(p);
      remaining.delete(art.id);
    }
  }
  return result;
};

const isRealArticleUrl = (u: string | null): boolean => {
  if (!u) return false;
  try { return new URL(u).pathname.replace(/\/+$/, "").length > 0; } catch { return false; }
};

// „NEU"-Merker: welche veröffentlichten Artikel-Links wurden schon angeklickt/geprüft?
// (localStorage, damit der Zustand Reloads übersteht — pro Browser des Operators)
const SEEN_KEY = "blog_seen_published";
const readSeen = (): Set<number> => {
  try { return new Set<number>(JSON.parse(localStorage.getItem(SEEN_KEY) || "[]")); } catch { return new Set<number>(); }
};
const markSeen = (id: number): void => {
  const s = readSeen(); s.add(id);
  try { localStorage.setItem(SEEN_KEY, JSON.stringify([...s])); } catch { /* Storage nicht verfügbar */ }
};

export function initBlogTab(m: HTMLElement, deps: MarketingDeps): void {
  const host = m.querySelector("[data-blog-queue]") as HTMLElement | null;
  const msg = m.querySelector('[data-msg="blog"]') as HTMLElement | null;
  if (!host || !msg) return;

  const transition = async (id: number, status: string): Promise<void> => {
    const r = await deps.api(`/api/marketing/content/${id}/status`, "POST", { status });
    msg.textContent = r.status === 200 ? "Gespeichert." : `Fehler (${r.status})`;
    await load();
  };

  const load = async (): Promise<void> => {
    const r = await deps.api<{ items: ContentItem[] }>("/api/marketing/content?channel=blog");
    const items = r.data?.items ?? [];
    host.textContent = "";
    if (!items.length) {
      const p = document.createElement("p");
      p.className = "dim";
      p.textContent = "Noch keine Blog-Entwürfe. „Themen + Artikel generieren“ startet den Motor (braucht ANTHROPIC_API_KEY unter Einstellungen).";
      host.appendChild(p);
      return;
    }
    const fc = forecast(items.filter((i) => i.status === "approved"));
    const seen = readSeen();
    for (const status of STATUS_ORDER) {
      const group = items.filter((i) => i.status === status);
      if (!group.length) continue;
      const h = document.createElement("h3");
      h.textContent = `${STATUS_LABEL[status] ?? status} (${group.length})`;
      host.appendChild(h);
      for (const item of group) {
        const card = document.createElement("details");
        card.className = "om-card";
        // Farbliche Status-Hinterlegung: Freigegeben = blau, Veröffentlicht = grün
        if (item.status === "approved") {
          card.style.cssText = "background:rgba(10,132,255,0.15);border-color:rgba(10,132,255,0.6);border-left:3px solid #0a84ff;";
        } else if (item.status === "published") {
          card.style.cssText = "background:rgba(48,209,88,0.15);border-color:rgba(48,209,88,0.6);border-left:3px solid #30d158;";
        }
        const sum = document.createElement("summary");
        sum.textContent = `#${item.id} · ${item.profile} · ${item.title ?? "(ohne Titel)"}`;
        // Glanceable badge im Summary
        const badge = document.createElement("span");
        badge.style.cssText = "margin-left:.5em;font-size:.85em;font-weight:600;white-space:nowrap;";
        if (item.status === "approved") {
          const d = fc.get(item.id);
          badge.textContent = d ? `🕒 vsl. ${fmtDate(d)}` : "🕒 geplant";
          badge.style.color = "#5ac8fa";
        } else if (item.status === "published") {
          const pd = item.published_at ?? item.approved_at ?? item.created_at;
          badge.textContent = pd ? `✅ live · ${fmtDate(pd)}` : "✅ live";
          badge.style.color = "#30d158";
        } else if (item.status === "needs_fix") {
          badge.textContent = "⚠ Fehler";
          badge.style.color = "#ff9f0a";
        }
        if (badge.textContent) sum.appendChild(badge);
        // „NEU"-Badge an noch nicht geprüften veröffentlichten Artikeln – nur, wenn sie höchstens 14 Tage alt sind.
        // 16.10.2026 (Uli: „Warum werden alle als NEU deklariert?“): Der Merker liegt im localStorage des Browsers; in einem
        // anderen Browser oder nach dem Löschen der Website-Daten galten sonst auch Artikel von Juli wieder als NEU.
        let neuBadge: HTMLElement | null = null;
        const liveSeit = item.published_at ?? item.approved_at ?? item.created_at;
        const frisch = !!liveSeit && Date.now() - new Date(liveSeit).getTime() < 14 * 864e5;
        if (item.status === "published" && frisch && isRealArticleUrl(item.target_url) && !seen.has(item.id)) {
          neuBadge = document.createElement("span");
          neuBadge.textContent = "NEU";
          neuBadge.style.cssText = "margin-left:.5em;font-size:.7em;font-weight:700;color:#fff;background:#ff453a;padding:.1em .5em;border-radius:999px;vertical-align:middle;letter-spacing:.04em;";
          sum.appendChild(neuBadge);
        }
        card.appendChild(sum);

        // Meta-Block (Datum / Status / URL) — sichtbar beim Aufklappen
        const meta = document.createElement("div");
        meta.style.cssText = "margin:.4em 0 .2em;font-size:.9em;line-height:1.5;";
        const line = (label: string, valueEl: Node | string): void => {
          const p = document.createElement("p");
          p.style.margin = "0";
          const b = document.createElement("strong");
          b.textContent = `${label} `;
          p.appendChild(b);
          if (typeof valueEl === "string") p.appendChild(document.createTextNode(valueEl));
          else p.appendChild(valueEl);
          meta.appendChild(p);
        };
        if (item.status === "approved") {
          if (item.approved_at) line("Freigegeben am:", fmtDate(item.approved_at));
          const d = fc.get(item.id);
          line("Voraussichtlich veröffentlicht:", d ? `${fmtDate(d)}, ca. 06:30 Uhr (Serverzeit)` : "wird eingeplant");
          const note = document.createElement("p");
          note.style.cssText = "margin:.2em 0 0;opacity:.6;font-size:.9em;";
          note.textContent = "Automatik: 1. & 15. jeden Monats, bis 1 Artikel pro Portal & Lauf (Schätzung — max. 2/Portal/30 Tage).";
          meta.appendChild(note);
        } else if (item.status === "published") {
          line("Status:", "✅ Veröffentlicht");
          if (isRealArticleUrl(item.target_url)) {
            const a = document.createElement("a");
            a.href = item.target_url as string;
            a.target = "_blank"; a.rel = "noopener";
            a.textContent = item.target_url as string;
            a.style.cssText = "color:#5ac8fa;text-decoration:underline;font-weight:600;word-break:break-all;";
            a.addEventListener("click", () => { markSeen(item.id); neuBadge?.remove(); neuBadge = null; });
            line("Live prüfen:", a);
          } else {
            line("Live-URL:", "(nicht gespeichert)");
          }
        } else if (item.status === "needs_fix") {
          const p = document.createElement("p");
          p.style.cssText = "margin:0;color:#e0a030;";
          p.textContent = "⚠ Veröffentlichung fehlgeschlagen (Verify-Gate). Bitte Artikel prüfen oder neu generieren.";
          meta.appendChild(p);
        }
        if (meta.childNodes.length) card.appendChild(meta);

        const body = document.createElement("pre");
        body.className = "om-body";
        body.textContent = item.body_text ?? "";
        card.appendChild(body);
        const actions = document.createElement("div");
        actions.className = "modal-actions inline";
        const mk = (label: string, to: string, primary = false): void => {
          const b = document.createElement("button");
          b.className = primary ? "btn-primary" : "btn-secondary";
          b.textContent = label;
          b.addEventListener("click", () => { void transition(item.id, to); });
          actions.appendChild(b);
        };
        if (item.status === "review") { mk("✓ Freigeben", "approved", true); mk("✗ Ablehnen", "rejected"); }
        if (item.status === "draft") { mk("→ Zur Freigabe", "review", true); mk("✗ Ablehnen", "rejected"); }
        if (item.status === "rejected") { mk("↻ Erneut prüfen", "review"); }
        if (item.status === "needs_fix") { mk("↻ Erneut prüfen", "review"); }
        card.appendChild(actions);
        host.appendChild(card);
      }
    }
  };

  m.querySelector('[data-action="run-blog-gen"]')?.addEventListener("click", () => {
    void (async () => {
      msg.textContent = "Starte Blog-Motor …";
      const r = await deps.api<{ stamp?: string }>("/api/marketing/blog-gen/run", "POST");
      if (r.status === 409) { msg.textContent = "Läuft bereits."; return; }
      if (r.status !== 200) { msg.textContent = `Fehler (${r.status})`; return; }
      msg.textContent = `Läuft (${r.data?.stamp}) — Entwürfe erscheinen nach Abschluss (Modal neu öffnen).`;
    })();
  });

  void load();
}
